import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import studioApiClient, { PublicationReviewUpdates } from '../api/studio';
import useAppContext from './useAppContext';
import useKlydoQuery from './useKlydoQuery';

type NotificationTab = 'klydos' | 'collections';

// Legacy key prefix, deliberately NOT renamed alongside the publication-review
// vocabulary: it is persisted in every artist's browser, and a new prefix would
// throw away their "last seen" markers.
const getStorageKey = (userId: string, tab: NotificationTab): string =>
  `review_status_last_seen_${tab}_${userId}`;

const readMarker = (userId: string, tab: NotificationTab): number | null => {
  try {
    const raw = localStorage.getItem(getStorageKey(userId, tab));
    if (!raw) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    // localStorage unavailable (e.g. private browsing with storage blocked)
    return null;
  }
};

const writeMarker = (
  userId: string,
  tab: NotificationTab,
  value: number,
): void => {
  try {
    localStorage.setItem(getStorageKey(userId, tab), String(value));
  } catch {
    // localStorage unavailable (e.g. private browsing with storage blocked)
  }
};

/**
 * Drives the "status changed" red dot on the Klydos / Collections nav tabs.
 *
 * On entry we fetch the epoch ms of the artist's most recent klydo/collection
 * approve-or-reject and compare it against a per-user "last seen" marker in
 * localStorage. A dot shows only when both values exist and the server value is
 * newer — i.e. a decision the artist hasn't seen yet. The marker is seeded on
 * first encounter (so pre-existing decisions never light up a fresh browser)
 * and re-synced whenever the artist is viewing that tab (which clears the dot).
 */
const useStatusNotifications = () => {
  const { user } = useAppContext();
  const userId = user?.id;
  const { pathname } = useLocation();

  const { data } = useKlydoQuery<PublicationReviewUpdates>({
    queryKey: ['publicationReviewUpdates', userId],
    queryFn: () => studioApiClient.getPublicationReviewUpdates(),
    enabled: !!userId,
    staleTime: 60 * 1000,
  });

  // Lazy-init from localStorage so the markers are correct on the very first
  // render. On a warm-cache remount react-query can hand us `data` in the same
  // commit; without this the seed effect below would read the initial nulls and
  // overwrite a real marker, silently swallowing an unseen decision.
  const [seen, setSeen] = useState<PublicationReviewUpdates>(() => ({
    klydos: userId ? readMarker(userId, 'klydos') : null,
    collections: userId ? readMarker(userId, 'collections') : null,
  }));

  // Reload the per-user markers if the artist becomes known after first render
  // (cold load: userId is undefined until auth resolves).
  useEffect(() => {
    if (!userId) return;
    setSeen({
      klydos: readMarker(userId, 'klydos'),
      collections: readMarker(userId, 'collections'),
    });
  }, [userId]);

  const markSeen = useCallback(
    (tab: NotificationTab, value: number) => {
      if (!userId) return;
      writeMarker(userId, tab, value);
      setSeen((prev) => ({ ...prev, [tab]: value }));
    },
    [userId],
  );

  // Seed on first encounter (nothing stored yet -> no dot) and clear while the
  // artist is viewing that tab. In both cases the marker catches up to the
  // latest server value, so a dot only appears for a *later* unseen decision.
  useEffect(() => {
    if (!userId || !data) return;
    (['klydos', 'collections'] as const).forEach((tab) => {
      const serverValue = data[tab];
      if (serverValue == null) return;
      const stored = seen[tab];
      const isViewingTab = pathname.startsWith(`/${tab}`);
      if ((stored == null || isViewingTab) && stored !== serverValue) {
        markSeen(tab, serverValue);
      }
    });
  }, [userId, data, pathname, seen, markSeen]);

  const hasNew = (tab: NotificationTab): boolean => {
    const serverValue = data?.[tab];
    const stored = seen[tab];
    return serverValue != null && stored != null && serverValue > stored;
  };

  return {
    hasNewKlydoStatus: hasNew('klydos'),
    hasNewCollectionStatus: hasNew('collections'),
  };
};

export default useStatusNotifications;
